import axios from "axios";
import { logout } from "../lib/auth";
import { tossUpAnalysysTableColumn } from "../config/tossUpAnalysys";

import { dataDifine, EditDataDifine } from "../config/dataInterface";
import { CONST } from "../config/const";
import { makeParams } from "./common";

/** トスアップ/クロージング分析 カレンダー・区分別内訳一括取得（accessDB2） */
export async function getTossUpAnalysysBreakdown(props: {
  year: string | number;
  month: string | number;
  analysys_type: 'tossup' | 'closing';
  toss_up_company_name?: string;
  toss_up_account_name?: string;
  closer_company_name?: string;
  closer_account_name?: string;
  category_name_list?: string;
}): Promise<{
  daily: { name: string; 合計: number }[];
  partner: { name: string; 合計: number }[];
  account: { name: string; 合計: number }[];
} | null> {
  try {
    const path = "tossUpAnalysysBreakdown";
    const params: Record<string, string | number> = {
      year: props.year,
      month: props.month,
      analysys_type: props.analysys_type,
    };
    if (props.toss_up_company_name) params.toss_up_company_name = props.toss_up_company_name;
    if (props.toss_up_account_name) params.toss_up_account_name = props.toss_up_account_name;
    if (props.closer_company_name) params.closer_company_name = props.closer_company_name;
    if (props.closer_account_name) params.closer_account_name = props.closer_account_name;
    if (props.category_name_list) params.category_name_list = props.category_name_list;
    const response = await axios.get(CONST.apiUrl2 + path, { params });
    if (!response.data.fetch_data) return null;
    return response.data.fetch_data as {
      daily: { name: string; 合計: number }[];
      partner: { name: string; 合計: number }[];
      account: { name: string; 合計: number }[];
    };
  } catch (error) {
    return null;
  }
}

/** トスアップ/クロージング分析：複数表示区分を一括取得（display_categories パラメータ使用） */
export async function getTossUpAnalysysMultiDivision(props: {
  [key: string]: number | string;
}, displayCategories: string[]): Promise<Record<string, {
  analysys: dataDifine[];
  columns: string[];
  all_data: dataDifine[];
}> | null> {
  try {
    const path = "tossUpAnalysys";
    const params: Record<string, string | number> = makeParams(tossUpAnalysysTableColumn.dataColumn, props);
    const countType = props.toss_up_analysys_count_type ?? "トスアップ日";
    params.toss_up_analysys_count_type = String(countType);
    if (props.toss_up_analysys_division_admin !== undefined && props.toss_up_analysys_division_admin !== "")
      params.toss_up_analysys_division_admin = String(props.toss_up_analysys_division_admin);
    if (props.toss_up_analysys_division_partner !== undefined && props.toss_up_analysys_division_partner !== "")
      params.toss_up_analysys_division_partner = String(props.toss_up_analysys_division_partner);
    const rawAggregation = props.toss_up_aggregation_item;
    const クロージング系 = ["クロージング-成功", "クロージング-エントリー数", "クロージング-有効アポ数", "クロージング-開通数", "クロージング-失敗"];
    let aggregationItem: string;
    if (countType === "トスアップ日") {
      aggregationItem = rawAggregation !== undefined && rawAggregation !== "" && !クロージング系.includes(String(rawAggregation))
        ? String(rawAggregation) : "トスアップ数";
    } else {
      aggregationItem = rawAggregation !== undefined && rawAggregation !== "" ? String(rawAggregation) : "クロージング-成功";
    }
    params.toss_up_aggregation_item = aggregationItem;
    const subCount = props.toss_up_analysys_sub_count;
    params.toss_up_analysys_sub_count =
      subCount === "なし" || subCount === undefined || subCount === "" ? "" : String(subCount);
    if (props.analysys_granularity_date_end_day !== undefined && props.analysys_granularity_date_end_day !== "")
      params.analysys_granularity_date_end_day = String(props.analysys_granularity_date_end_day);
    params.display_categories = displayCategories.join(",");
    const response = await axios.get(CONST.apiUrl + path, { params });
    if (!response.data.fetch_data || response.data.fetch_data === "null") return null;
    const result: Record<string, { analysys: dataDifine[]; columns: string[]; all_data: dataDifine[] }> = {};
    for (const [key, value] of Object.entries(response.data.fetch_data as Record<string, any[]>)) {
      const arr = value as any[];
      if (arr.length > 0 && arr[0].full_result) {
        result[key] = {
          analysys: arr[0].full_result.data || [],
          columns: arr[0].full_result.analysys_columns || [],
          all_data: arr[0].full_result.all_data || [],
        };
      }
    }
    return result;
  } catch (error) {
    return null;
  }
}

//  トスアップ分析
export async function getTossUpAnalysys(props: {
  [key: string]: number | string;
}) {
  try {
    const path = "tossUpAnalysys";
    const params: Record<string, string | number> = makeParams(tossUpAnalysysTableColumn.dataColumn, props);
    // 計上タイプは serch:false のため makeParams に含まれない。バックエンド必須のためここで付与する。
    const countType = props.toss_up_analysys_count_type ?? "トスアップ日";
    params.toss_up_analysys_count_type = String(countType);
    // 表示区分を明示付与（ダッシュボード「グループ別」が正しく送るため）
    if (props.toss_up_analysys_division_admin !== undefined && props.toss_up_analysys_division_admin !== "")
      params.toss_up_analysys_division_admin = String(props.toss_up_analysys_division_admin);
    if (props.toss_up_analysys_division_partner !== undefined && props.toss_up_analysys_division_partner !== "")
      params.toss_up_analysys_division_partner = String(props.toss_up_analysys_division_partner);
    // 集計項目を明示的に付与。トスアップ日のときは必ずトスアップ系にし、クロージング系が渡っていても上書きする
    const rawAggregation = props.toss_up_aggregation_item;
    const クロージング系 = ["クロージング-成功", "クロージング-エントリー数", "クロージング-有効アポ数", "クロージング-開通数", "クロージング-失敗"];
    let aggregationItem: string;
    if (countType === "トスアップ日") {
      aggregationItem = rawAggregation !== undefined && rawAggregation !== "" && !クロージング系.includes(String(rawAggregation))
        ? String(rawAggregation)
        : "トスアップ数";
    } else {
      aggregationItem = rawAggregation !== undefined && rawAggregation !== "" ? String(rawAggregation) : "クロージング-成功";
    }
    params.toss_up_aggregation_item = aggregationItem;
    // サブカラム「なし」は空で送る
    const subCount = props.toss_up_analysys_sub_count;
    params.toss_up_analysys_sub_count =
      subCount === "なし" || subCount === undefined || subCount === "" ? "" : String(subCount);
    // ダッシュボードKPIと同一期間にするため dataColumn にないパラメータを付与
    if (props.analysys_granularity_date_end_day !== undefined && props.analysys_granularity_date_end_day !== "")
      params.analysys_granularity_date_end_day = String(props.analysys_granularity_date_end_day);
    const response = await axios.get(CONST.apiUrl + path, {
      params,
    });
    if (response.data.fetch_data === "null") {
      return null;
    }
    const analysys: dataDifine[] = response.data.fetch_data[0].full_result.data;
    const columns: string[] =
      response.data.fetch_data[0].full_result.analysys_columns;
    const all_data: dataDifine[] =
      response.data.fetch_data[0].full_result.all_data;
    return { analysys, columns, all_data };
  } catch (error) {
    // logout();
  }
}
